import ProductSearchAbstract from './ProductSearchAbstract';

import ParamPairs from '../../lib/ParamPairs';

import triggerCustom from '../../lib/triggerCustomEvent';
import parseHTML from '../../lib/parseHTML';
import Accordion from '../Accordion/Accordion';
import AutoLoadItems from '../../lib/AutoLoadItems';

export default class ProductSearchDesktop extends ProductSearchAbstract {
	private form: HTMLFormElement;
	private formFieldEl: HTMLFormControlsCollection;
	private accordion: Accordion;
	private autoLoader: AutoLoadItems;

	constructor() {
		super();

		this.form = document.getElementById('productSearchForm') as HTMLFormElement;
		this.formFieldEl = this.form.elements;
		this.excludedParams.push('pcat');

		this.bindDesktopInputHandlers();
		this.buildFilterTray(
			'filterTrayWrapper', {
				excludedQueries: this.excludedParams,
				clearFiltersCB: this.clearFiltersCB
			});
		this.buildPopstate();

		this.accordion = new Accordion('filterHeading', 'filterGroup');
		this.openFilterAccordions();
		this.autoLoader = new AutoLoadItems({
			frameId: 'productSearchList__inner',
			nextClass: 'btn--next',
			resultsClass: 'productListItem',
			paginationClass: 'pagination'
		});
	}

	// NOTE: This allows anything else using onpopstate to execute per Josh's recommendation -SB 2017-11-15
	private buildPopstate(): void {
		const oldOnpopstate: Function = window.onpopstate;
		if (oldOnpopstate) {
			window.onpopstate = (event: Event) => {
				this.params = new ParamPairs();
				this.doHtmlAjax(this.htmlAjaxAction);
				oldOnpopstate.apply(event);
			};
		} else {
			window.onpopstate = (event: Event) => {
				this.params = new ParamPairs();
				this.doHtmlAjax(this.htmlAjaxAction);
			};
		}
	}

	private bindDesktopInputHandlers(): void {
		this.form = document.getElementById('productSearchForm') as HTMLFormElement;
		const handleInputUpdate: EventListener = (event: Event) => {
			triggerCustom(document, 'triggerWaiting');

			let updatedEl: (HTMLInputElement | HTMLSelectElement) = event.target as HTMLInputElement | HTMLSelectElement;

			const elName: string = updatedEl.name;
			const elVal: string = updatedEl.value;
			const elId: string = updatedEl.id;
			const elType: string = updatedEl.type;
			// NOTE: The split is to grab only the text, not the quantity -SB 2017-12-05
			const elLabel: string = this.form.querySelector(`[for="${elId}"]`).textContent.split('(')[0];

			if (elType === 'text') {
				this.params.update(elName, elVal);
			} else if (updatedEl.tagName === 'SELECT') {
				this.params.update(elName, elVal);
				this.ga.trackEvent('Product Search Filter', 'Select', elLabel);
			} else if (elType === 'radio') {
				this.params.update(elName, elVal);
				this.ga.trackEvent('Product Search Filter', 'Select', elLabel);
			} else if (elType === 'checkbox') {
				updatedEl = <HTMLInputElement>updatedEl;

				if (updatedEl.checked) {
					this.params.add(elName, elVal);
					this.ga.trackEvent('Product Search Filter', 'Select', elVal);
				} else {
					this.params.removeValue(elName, elVal);
				}
			}

			const newURL: string = this.params.buildQueryString();
			history.pushState({}, document.title, newURL);
			this.doHtmlAjax(this.htmlAjaxAction);
		};
		this.form.addEventListener('change', handleInputUpdate);

		const handleSubmit: EventListener = (event: Event) => {
			event.preventDefault();

			triggerCustom(document, 'triggerWaiting');

			const newURL: string = this.params.buildQueryString();
			history.pushState({}, document.title, newURL);
			this.doHtmlAjax(this.htmlAjaxAction);
		};
		this.form.addEventListener('submit', handleSubmit);

		const paginationEl: HTMLCollectionOf<HTMLAnchorElement> =
			document.getElementsByClassName('page') as HTMLCollectionOf<HTMLAnchorElement>;
		const handlePagination: EventListener = (event: Event) => {
			event.preventDefault();

			const element: HTMLAnchorElement = event.currentTarget as HTMLAnchorElement;
			const newURL: string = element.href;

			history.pushState({}, document.title, newURL);
			this.doHtmlAjax(this.htmlAjaxAction);
		};
		for (let i: number = 0; i < paginationEl.length; i++) {
			const element: HTMLAnchorElement = paginationEl[i];
			element.addEventListener('click', handlePagination);
		}
	}
	private htmlAjaxAction = (response: string): void => {
		const newPage: Document = parseHTML(response);
		if (window.scroll) {
			window.scroll(0, 0);
		}
		this.replaceMain(newPage);
		this.buildFilterTray(
			'filterTrayWrapper', {
				excludedQueries: this.excludedParams,
				clearFiltersCB: this.clearFiltersCB
			});

		this.bindDesktopInputHandlers();
		this.accordion.buildAccordions();
		this.autoLoader.init();
		this.openFilterAccordions();
	}

	private replaceMain(newPage: Document): void {
		const oldMain: Element = document.getElementById('mainContent');
		const newMain: Element = document.importNode(newPage.getElementById('mainContent'), true);
		const footerEl: Element = document.getElementById('mainFooter');
		document.body.removeChild(oldMain);
		document.body.insertBefore(newMain, footerEl);
	}

	private doHtmlAjax(handleResponse: any, requestUrl: string = window.location.href): void {
		function status(response: Response): Promise<any> {
			if (response.ok) {
				return Promise.resolve(response);
			} else {
				return Promise.reject(new Error(response.statusText));
			}
		}

		fetch(requestUrl)
			.then(status)
			.then(response => response.text())
			.then(handleResponse)
			.catch(error => {
				triggerCustom(document, 'clearWaiting');
				console.warn(error);
			});
	}

	private clearFiltersCB: EventListener = this.destroyFilterTrayWith(() => {
		triggerCustom(document, 'triggerWaiting');

		const queryEl: HTMLInputElement = this.form.querySelector('#q') as HTMLInputElement;
		queryEl.value = '';

		const newURL: string = this.params.buildQueryString();
		history.pushState({}, document.title, newURL);
		this.doHtmlAjax(this.htmlAjaxAction);
	});

	private openFilterAccordions(): void {
		const closedHeadings = document.querySelectorAll('.filterHeading:not(.filterHeading--open)');
		const filterGroups = document.querySelectorAll('.filterHeading:not(.filterHeading--open) + .filterGroup');
		for (let i = 0; i < filterGroups.length; i++) {
			let isActive = false;
			const inputEl = filterGroups[i].getElementsByTagName('input');
			for (let j = 0; j < inputEl.length; j++) {
				if (inputEl[j].checked) {
					isActive = true;
					break;
				}
			}

			if (isActive) {
				this.accordion.openAccordion(closedHeadings[i] as HTMLElement);
			}
		}
	}
}